/** Features 页文案与配图（对齐飞书需求文档） */

const IMG = {
	privacySettings: '/images/gbwaps/features/privacy-settings.png',
	themes: '/images/gbwaps/features/themes-4k.webp',
	messageScheduler: '/images/gbwaps/features/message-scheduler.webp',
	statusSave: '/images/gbwaps/features/save-status-videos.png',
	dnd: '/images/gbwaps/features/dnd.webp',
};

export const FEATURES_PAGE_INTRO =
	'Explore the features that make GB WhatsApp different from the official WhatsApp app. From advanced privacy controls and custom themes to larger media sharing and extra chat tools, this page gives you a quick overview of what GB WhatsApp can do.';

export const FEATURES_PAGE_NOTE =
	'GB WhatsApp is an unofficial modified version of WhatsApp. Available features and settings can vary between builds, so always check the options available in your installed version.';

export const POPULAR_FEATURES = [
	{ feature: 'Hide Online Status', what: 'Control whether contacts can see when you are online.' },
	{ feature: 'Hide Read Receipts', what: 'Hide blue ticks and control message-read indicators.' },
	{ feature: 'Anti-Delete Messages', what: 'Keep messages visible when a sender deletes them for everyone.' },
	{ feature: 'Custom Themes', what: 'Change colors, fonts, chat styles, and other parts of the interface.' },
	{ feature: 'Large File Sharing', what: 'Share larger videos, photos, and documents than standard WhatsApp allows.' },
	{ feature: 'Status Download', what: 'Save photos and videos from WhatsApp Status to your device.' },
	{ feature: 'Dual Account', what: 'Use more than one WhatsApp number on a supported device or build.' },
	{ feature: 'Chat Lock & DND', what: 'Add extra control over private chats and notifications.' },
];

export const FEATURE_CATEGORIES = [
	{
		id: 'privacy',
		theme: 'privacy',
		h2: 'Privacy & Visibility Features',
		intro:
			'Privacy controls are one of the main reasons people explore GB WhatsApp. Depending on the version you use, you can find more options for controlling what other contacts can see.',
		items: [
			{
				id: 'hide-online-status',
				h3: 'Hide Online Status',
				paragraphs: [
					'GB WhatsApp can let you appear offline even when you are using the app. You may also be able to freeze your last seen status, giving you more control over when contacts can see your activity.',
				],
				image: {
					src: IMG.privacySettings,
					alt: 'GB WhatsApp Privacy settings dialog with hide blue ticks, typing, and anti-delete options',
				},
				relatedGuide: {
					to: '/blog/hide-online-status/',
					title: 'How to Hide Online Status in GB WhatsApp',
					excerpt:
						'Learn how to hide your online status in GB WhatsApp and WhatsApp, including Last Seen differences and extra privacy options.',
					image: '/images/gbwaps/blog/hide-online-status.png',
					imageAlt: 'Hide online status in GB WhatsApp guide',
				},
			},
			{
				id: 'hide-read-receipts',
				h3: 'Hide Read Receipts',
				paragraphs: [
					'You can hide blue ticks so other people cannot immediately tell that you have opened their messages. Some builds also provide separate controls for typing and recording indicators.',
				],
				relatedGuide: {
					to: '/blog/how-to-hide-blue-ticks-in-gb-whatsapp/',
					title: 'How to Hide Blue Ticks in GB WhatsApp',
					excerpt:
						'See how read receipts work, how to disable them, and how GB WhatsApp privacy options differ from official WhatsApp.',
					image: '/images/gbwaps/blog/hide-blue-ticks.webp',
					imageAlt: 'Hide blue ticks in GB WhatsApp guide',
				},
			},
			{
				id: 'hide-typing-recording',
				h3: 'Hide Typing & Recording Status',
				paragraphs: [
					'When you type a message or record a voice note, WhatsApp normally shows an activity indicator. Supported GB WhatsApp builds can give you additional control over these indicators.',
				],
			},
			{
				id: 'anti-delete',
				h3: 'Anti-Delete Messages',
				paragraphs: [
					'The anti-delete feature can keep a copy of a message on your device after the sender chooses to delete it for everyone. This can be useful when you need to refer back to a message, but it should always be used with respect for other people’s privacy.',
				],
				relatedGuide: {
					title: 'How to Use Anti-Delete Messages in GB WhatsApp APK',
				},
			},
		],
	},
	{
		id: 'customization',
		theme: 'customization',
		h2: 'Customization Features',
		intro:
			'If you find the standard WhatsApp interface too limited, GB WhatsApp offers much more control over the way the app looks.',
		items: [
			{
				id: 'custom-themes',
				h3: 'Custom Themes',
				paragraphs: [
					'You can change the overall appearance of GB WhatsApp with custom themes. Depending on the build, theme options can change colors, backgrounds, chat bubbles, fonts, icons, and other interface elements.',
					'Popular styles include AMOLED dark themes, minimal designs, colorful themes, and iOS-inspired interfaces.',
				],
				relatedGuide: {
					title: 'GB WhatsApp New Trending Themes You Must Try in 2026',
				},
			},
			{
				id: 'fonts-chat-styles',
				h3: 'Fonts & Chat Styles',
				paragraphs: [
					'Supported versions can provide additional font styles and interface settings that are not normally available in official WhatsApp. This lets you give your chats a more personalized look without rooting your Android phone.',
				],
			},
			{
				id: 'wallpapers-interface',
				h3: 'Wallpapers & Interface Controls',
				paragraphs: [
					'Beyond chat wallpapers, some builds provide additional controls for conversation screens, notification styles, launcher icons, and other parts of the interface.',
					'Keep in mind that heavily customized themes may use more system resources. If the interface feels slow, switching to a simpler theme can help.',
				],
				image: {
					src: IMG.themes,
					alt: 'GB WhatsApp custom themes and wallpapers interface preview',
				},
			},
		],
	},
	{
		id: 'messaging',
		theme: 'messaging',
		h2: 'Messaging Features',
		intro: 'GB WhatsApp also adds extra controls around everyday messaging.',
		items: [
			{
				id: 'auto-reply',
				h3: 'Auto Reply',
				paragraphs: [
					'Some builds include an auto-reply tool that can send a predefined response when you are unavailable. This can be useful for simple personal or work-related messages.',
				],
			},
			{
				id: 'message-management',
				h3: 'Message Management',
				paragraphs: [
					'Depending on the version, you may find additional options for managing chats, pinning conversations, changing message behavior, or controlling notifications.',
					'Because these tools are modified features, their names and exact settings can change between GB WhatsApp versions.',
				],
				image: {
					src: IMG.messageScheduler,
					alt: 'GB WhatsApp message scheduler and chat management tools',
				},
			},
			{
				id: 'more-privacy-chats',
				h3: 'More Privacy Controls for Chats',
				paragraphs: [
					'GB WhatsApp combines messaging with additional visibility controls, allowing you to decide more precisely which activity indicators other people can see.',
				],
			},
		],
	},
	{
		id: 'media',
		theme: 'media',
		h2: 'Media & File Sharing Features',
		intro:
			'Standard WhatsApp places limits on file sizes, media quality, and some sharing options. GB WhatsApp builds often try to make these limits more flexible.',
		items: [
			{
				id: 'large-file-sharing',
				h3: 'Large File Sharing',
				paragraphs: [
					'Supported versions can let you send larger videos, documents, photos, and other files than the standard app allows.',
					'This can be useful when sharing long family videos, business files, product catalogs, or high-resolution media.',
				],
				relatedGuide: {
					title: 'Large File Sharing & Media Limits in GB WhatsApp',
				},
			},
			{
				id: 'higher-quality-media',
				h3: 'Higher-Quality Media',
				paragraphs: [
					'Some GB WhatsApp versions provide additional options for sending photos and videos with less compression. The exact quality and size limits depend on the build.',
				],
			},
			{
				id: 'status-download',
				h3: 'Status Download',
				paragraphs: [
					'GB WhatsApp can provide a built-in option for saving photos and videos from other people’s Status updates to your device.',
					'Always consider the privacy and copyright of content before saving or sharing someone else’s media.',
				],
				image: {
					src: IMG.statusSave,
					alt: 'Save WhatsApp Status videos and photos from GB WhatsApp',
				},
				relatedGuide: {
					title: 'Status Download & Gallery Save',
				},
			},
		],
	},
	{
		id: 'account',
		theme: 'account',
		h2: 'Account & Chat Management',
		intro:
			'GB WhatsApp can also provide additional options for people who manage multiple numbers or want more control over their conversations.',
		items: [
			{
				id: 'dual-account',
				h3: 'Dual Account',
				paragraphs: [
					'Some builds support using two WhatsApp numbers on the same Android phone. This can be useful for separating personal and work conversations without carrying two devices.',
					'If your phone already includes a built-in Dual Apps, App Clone, or similar function, that may be a more stable option.',
				],
				relatedGuide: {
					title: 'Dual Account & Chat Lock on One Phone',
				},
			},
			{
				id: 'chat-lock',
				h3: 'Chat Lock',
				paragraphs: [
					'Supported builds can provide additional ways to protect individual conversations from casual access. Chat locking can be useful when you share your phone with family members or colleagues.',
				],
			},
			{
				id: 'dnd-mode',
				h3: 'DND Mode',
				paragraphs: [
					'Some versions include a Do Not Disturb option that can temporarily reduce WhatsApp notifications without affecting your phone’s other apps.',
				],
				image: {
					src: IMG.dnd,
					alt: 'GB WhatsApp Do Not Disturb mode settings',
				},
			},
		],
	},
];

export const FEATURES_COMPARISON_ROWS = [
	{ feature: 'Hide read receipts', official: '✅', gb: '✅' },
	{ feature: 'Advanced online visibility controls', official: 'Limited', gb: '✅' },
	{ feature: 'Custom themes', official: 'Limited', gb: '✅' },
	{ feature: 'Custom fonts', official: 'Limited', gb: '✅' },
	{ feature: 'Large file sharing', official: 'Limited', gb: 'Often available' },
	{ feature: 'Status download', official: '❌', gb: '✅' },
	{ feature: 'Anti-delete messages', official: '❌', gb: '✅' },
	{ feature: 'Dual account options', official: 'Device-dependent', gb: 'Often available' },
	{ feature: 'Official Play Store distribution', official: '✅', gb: '❌' },
	{ feature: 'Official Meta support', official: '✅', gb: '❌' },
	{ feature: 'Ban / account risk', official: 'Lower', gb: 'Higher' },
	{ feature: 'Feature availability', official: 'Consistent', gb: 'Varies by build' },
];

export const FEATURES_USEFUL_GROUPS = [
	{
		title: 'For more privacy',
		text: 'Start with Hide Online Status, Hide Read Receipts, and Hide Typing Status.',
	},
	{
		title: 'For customization',
		text: 'Try Custom Themes, Fonts, Wallpapers, and Chat Styles.',
	},
	{
		title: 'For media sharing',
		text: 'Look at Large File Sharing, Higher-Quality Media, and Status Download.',
	},
	{
		title: 'For multiple numbers',
		text: 'Check whether your build supports Dual Account, or use your phone’s built-in app-cloning feature instead.',
	},
	{
		title: 'For message control',
		text: 'Anti-Delete, Chat Lock, Auto Reply, and DND can provide additional options depending on your version.',
	},
];

export const FEATURES_IMPORTANT = [
	'GB WhatsApp is not an official WhatsApp application. That means its extra features come with trade-offs that you should understand before using them.',
	'Feature availability can differ between GB WhatsApp builds, and an option shown in one version may not exist in another. Privacy features also do not make an unofficial APK automatically secure.',
	'Before installing or updating GB WhatsApp, download the APK from a source you trust, check the permissions requested by the app, keep important chats backed up, and avoid using unofficial modifications for sensitive or highly confidential communications.',
	'There is also a greater account risk compared with the official WhatsApp app because modified clients are not part of WhatsApp’s official distribution.',
];

export const FEATURES_FAQ = [
	{
		q: 'What are the main features of GB WhatsApp?',
		a: 'The most popular features include advanced privacy controls, custom themes, larger media sharing, status downloading, anti-delete messages, dual-account options, and additional chat controls.',
	},
	{
		q: 'Does GB WhatsApp have more features than official WhatsApp?',
		a: 'Yes. GB WhatsApp adds many customization, privacy, and media-sharing options that are not available in the standard WhatsApp app. However, the exact features depend on the GB WhatsApp build.',
	},
	{
		q: 'Can I hide my online status with GB WhatsApp?',
		a: 'Many GB WhatsApp builds provide additional online and last-seen privacy controls. The exact settings can vary between versions.',
	},
	{
		q: 'Can GB WhatsApp save deleted messages?',
		a: 'Its anti-delete feature can keep certain messages visible when the sender deletes them for everyone. It does not recover every type of deleted content or restore messages after an app reinstall without a backup.',
	},
	{
		q: 'Are all GB WhatsApp features safe?',
		a: 'Not necessarily. GB WhatsApp is an unofficial modified app, so its features and security can vary by build. Extra privacy controls should not be confused with official security or support.',
	},
];

/** FAQ Schema（Features 页） */
export const FEATURES_FAQ_SCHEMA = {
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	mainEntity: FEATURES_FAQ.map((item) => ({
		'@type': 'Question',
		name: item.q,
		acceptedAnswer: {
			'@type': 'Answer',
			text: item.a,
		},
	})),
};

/** @deprecated 兼容旧引用 */
export const FEATURES_PAGE_GUIDES = [];
export const FEATURES_PAGE_CARDS = FEATURES_PAGE_GUIDES;
export const FEATURES_SECTIONS = [];
